import { getLang } from "../bootstrap/copy";
import type { Config } from "../types";
import {
  getValueWithKeyFromSearch,
  SEARCH_ACR,
  SEARCH_CODE,
  SEARCH_ERROR,
  SEARCH_STATE,
} from "./helpers";

let IS_TUNNISTUS_PROD = false;
let TUNNISTUS_ROOT = "";
let TUNNISTUS_CLIENTID = "";
const FAKE_BANK_ID = "auth.fake.bank";
let FAKE_BANK_ROOT = "";
let ENV = "";
const FAKE_BANK_ENVS = ["sandbox", "demo", "local"];

export function initTunnistus(config: Config) {
  IS_TUNNISTUS_PROD = config.oauth_telia_tunnistus_prod;
  TUNNISTUS_ROOT = config.oauth_telia_tunnistus_url;
  TUNNISTUS_CLIENTID = config.oauth_telia_tunnistus_client_id;
  ENV = config.env;
  FAKE_BANK_ROOT = FAKE_BANK_ENVS.includes(ENV)
    ? config.core_api_origin + "/test/fake_bank/auth"
    : "";
}

export interface StrongAuthService {
  id: string;
  name: string;
  logo: string;
  logo1x: string;
  logo2x: string;
  logo3x: string;
  logo4x: string;
  rank: number;
}

// rank is determined by
// https://www.suomenpankki.fi
// /fi/Tilastot
// /rahalaitosten-tase-lainat-ja-talletukset-ja-korot
// /tiedotehistoria/2018/markkinaosuudet/
// Mobile ID is has 200k users
// Mobile ID #1 ranking >
// promote mobiilivarmenne
// + it is often the first in public services (familiar place)

const getStrongAuthServices = (): StrongAuthService[] => [
  {
    id: "mpki.telia.1",
    name: "Mobiilivarmenne",
    logo: "mobiilivarmenne.png",
    logo1x: "mobiilivarmenne1x.webp",
    logo2x: "mobiilivarmenne2x.webp",
    logo3x: "mobiilivarmenne3x.webp",
    logo4x: "mobiilivarmenne4x.webp",
    rank: 1,
  },
  {
    id: "oidc.aktia.1",
    name: "Aktia",
    logo: "aktia.png",
    logo1x: "aktia1x.webp",
    logo2x: "aktia2x.webp",
    logo3x: "aktia3x.webp",
    logo4x: "aktia4x.webp",
    rank: 6,
  },
  {
    id: "oidc.alandsbanken.1",
    name: "Ålandsbanken",
    logo: "alandsbanken.png",
    logo1x: "alandsbanken1x.webp",
    logo2x: "alandsbanken2x.webp",
    logo3x: "alandsbanken3x.webp",
    logo4x: "alandsbanken4x.webp",
    rank: 10,
  },
  {
    id: IS_TUNNISTUS_PROD ? "oidc.danskebank.1" : "oidc.danske.1",
    name: "Danske Bank",
    logo: "danskebank.png",
    logo1x: "danskebank1x.webp",
    logo2x: "danskebank2x.webp",
    logo3x: "danskebank3x.webp",
    logo4x: "danskebank4x.webp",
    rank: 4,
  },
  {
    id: "oidc.nordea.1",
    name: "Nordea",
    logo: "nordea.png",
    logo1x: "nordea1x.webp",
    logo2x: "nordea2x.webp",
    logo3x: "nordea3x.webp",
    logo4x: "nordea4x.webp",
    rank: 3,
  },
  {
    id: "oidc.omasp.1",
    name: "Oma SP",
    logo: "omasp.png",
    logo1x: "omasp1x.webp",
    logo2x: "omasp2x.webp",
    logo3x: "omasp3x.webp",
    logo4x: "omasp4x.webp",
    rank: 11,
  },
  {
    id: IS_TUNNISTUS_PROD ? "saml.op.1" : "saml.op.fi",
    name: "Osuuspankki",
    logo: "op.png",
    logo1x: "op1x.webp",
    logo2x: "op2x.webp",
    logo3x: "op3x.webp",
    logo4x: "op4x.webp",
    rank: 2,
  },
  {
    id: "oidc.pop.1",
    name: "POP Pankki",
    logo: "poppankki.png",
    logo1x: "poppankki1x.webp",
    logo2x: "poppankki2x.webp",
    logo3x: "poppankki3x.webp",
    logo4x: "poppankki4x.webp",
    rank: 9,
  },
  {
    id: "oidc.sp.1",
    name: "Säästöpankki",
    logo: "saastopankki.png",
    logo1x: "saastopankki1x.webp",
    logo2x: "saastopankki2x.webp",
    logo3x: "saastopankki3x.webp",
    logo4x: "saastopankki4x.webp",
    rank: 5,
  },
  {
    id: "oidc.spankki.1",
    name: "S-Pankki",
    logo: "spankki.png",
    logo1x: "spankki1x.webp",
    logo2x: "spankki2x.webp",
    logo3x: "spankki3x.webp",
    logo4x: "spankki4x.webp",
    rank: 8,
  },
];

const getFakeBank: StrongAuthService = {
  id: FAKE_BANK_ID,
  name: "Fake Bank",
  logo: "fake.jpeg",
  logo1x: "fake1x.webp",
  logo2x: "fake2x.webp",
  logo3x: "fake3x.webp",
  logo4x: "fake4x.webp",
  rank: 99,
};

export const getStrongAuthServicesSorted = (): StrongAuthService[] => {
  const services: StrongAuthService[] = getStrongAuthServices().sort(
    (a, b) => a.rank - b.rank,
  );
  return FAKE_BANK_ENVS.includes(ENV) ? services.concat(getFakeBank) : services;
};

const redir = (redirect: string): string => `&redirect_uri=${redirect}/return`;
const stateParam = (state: string): string => "&state=" + state;
const nonce = (nonceId: string): string => "&nonce=" + nonceId;
const locale = (lang: string): string => "&ui_locales=" + lang;
const acrValues = (service: string): string => "&acr_values=" + service;

export const strongAuthUrl = (
  service: string,
  nonceId: string,
  redirect: string,
  state: string,
): string => {
  const root = service === FAKE_BANK_ID && FAKE_BANK_ENVS.includes(ENV)
    ? FAKE_BANK_ROOT
    : TUNNISTUS_ROOT;
  return (
    root +
    "?client_id=" +
    TUNNISTUS_CLIENTID +
    "&response_type=code&scope=openid" +
    redir(redirect) +
    nonce(nonceId) +
    stateParam(state) +
    locale(getLang().slice(0, 2)) +
    acrValues(service)
  );
};

export const getStrongAuthCodeFromSearch = (search: string): string =>
  getValueWithKeyFromSearch(search, SEARCH_CODE) || "";
export const getAcrValuesFromSearch = (search: string): string =>
  getValueWithKeyFromSearch(search, SEARCH_ACR) || "";
export const getStrongAuthStateFromSearch = (search: string): string =>
  getValueWithKeyFromSearch(search, SEARCH_STATE) || "";
export const getStrongAuthErrorFromSearch = (search: string): string =>
  getValueWithKeyFromSearch(search, SEARCH_ERROR) || "error";
